import type { KeyboardEvent } from 'react';

import type { A11y } from '@snowit/icetypes';
import type { A11yEvent, ToggleOnClick } from '@snowit/icetypes/events';

import { Keyboard } from '@snowit/iceconst';
import testId from '@snowit/utils/testId';

import Loader from '../Loaders/LoaderSpinner';

import cx from 'classnames';
import s from './Toggle.module.scss';

export interface IToggle {
  value?: boolean;
  name: string;
  onClick: (input: ToggleOnClick) => void;
  loading?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  test?: string;
  a11y?: A11y;
}

const Toggle = (props: IToggle) => {
  const { value, name, onClick, loading, disabled, id, className, test, a11y } = props;

  const handleClick = (event: A11yEvent<HTMLDivElement>) => {
    if (loading) return;
    if (disabled) return;
    if (typeof onClick === 'function') {
      onClick({ name, id, value: !value, event });
    }
  };

  const handleKeydown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== Keyboard.ENTER) return;
    handleClick(event);
  };

  return (
    <div
      id={id}
      className={cx(s.wrapper, className, {
        [s.checked]: Boolean(value),
        [s.disabled]: disabled || loading,
      })}
      onClick={handleClick}
      onKeyDown={handleKeydown}
      role={'switch'}
      tabIndex={disabled || loading ? -1 : 0}
      aria-checked={Boolean(value)}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      aria-label={a11y?.label}
      {...testId(test)}
    >
      <div className={s.toggle} {...testId('toggle', test)}>
        {loading && <Loader className={s.loader} size={10} />}
      </div>
    </div>
  );
};

export default Toggle;
